import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Phone, Save, X, Check, Trash2, Pencil, Merge } from "lucide-react";

function MergeNameModal({ names, onConfirm, onClose }) {
  const uniqueNames = [...new Set(names)];
  const [customName, setCustomName] = useState(uniqueNames[0] || "");

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 bg-black/40 flex items-center justify-center z-10 rounded-xl"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl p-5 max-w-xs w-full shadow-2xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <Merge className="w-5 h-5 text-indigo-600" />
          <h3 className="text-base font-bold text-slate-900">Merge Contacts</h3>
        </div>
        <p className="text-sm text-slate-600 mb-3">Choose a name for the merged contact:</p>
        <div className="space-y-2 mb-3">
          {uniqueNames.map((name) => (
            <button
              key={name}
              onClick={() => setCustomName(name)}
              className={`w-full text-left p-2.5 rounded-lg border transition-all text-sm font-medium ${
                customName === name
                  ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                  : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
              }`}
            >
              {name}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 mb-4">
          <Pencil className="w-4 h-4 text-slate-400 shrink-0" />
          <Input
            data-testid="bulk-merge-custom-name"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Or type a custom name"
            className="text-sm"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="bulk-confirm-merge-btn"
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
            size="sm"
            onClick={() => onConfirm(customName.trim())}
            disabled={!customName.trim()}
          >
            <Merge className="w-3.5 h-3.5 mr-1" />Merge
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function BulkPhoneEditor({ contacts, onSave, onClose, onDeleteContact, onMergeContacts, onRenameContact }) {
  const [phones, setPhones] = useState(() => {
    const map = {};
    contacts.forEach((c) => { map[c.id] = c.phone || ""; });
    return map;
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingNameId, setEditingNameId] = useState(null);
  const [editNameValue, setEditNameValue] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [showMergeModal, setShowMergeModal] = useState(false);
  const firstInputRef = useRef(null);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (firstInputRef.current) firstInputRef.current.focus();
  }, []);

  useEffect(() => {
    if (editingNameId && nameInputRef.current) nameInputRef.current.focus();
  }, [editingNameId]);

  const handleSave = async () => {
    setSaving(true);
    const updates = Object.entries(phones).map(([id, phone]) => ({ id, phone: phone.trim() }));
    await onSave(updates);
    setSaving(false);
    setSaved(true);
    setTimeout(() => onClose(), 800);
  };

  const handleConfirmDelete = (contactId) => {
    onDeleteContact(contactId);
    setConfirmDeleteId(null);
    setSelected((prev) => { const next = new Set(prev); next.delete(contactId); return next; });
  };

  const handleStartRename = (contact) => {
    setEditingNameId(contact.id);
    setEditNameValue(contact.name);
  };

  const handleSaveRename = async (contactId) => {
    const trimmed = editNameValue.trim();
    if (!trimmed) { setEditingNameId(null); return; }
    await onRenameContact(contactId, trimmed);
    setEditingNameId(null);
  };

  const handleRenameKeyDown = (e, contactId) => {
    if (e.key === "Enter") handleSaveRename(contactId);
    if (e.key === "Escape") setEditingNameId(null);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleMergeClick = () => {
    const selectedContacts = contacts.filter((c) => selected.has(c.id));
    const uniqueNames = [...new Set(selectedContacts.map((c) => c.name))];
    if (uniqueNames.length === 1) {
      onMergeContacts([...selected], uniqueNames[0]);
      setSelected(new Set());
    } else {
      setShowMergeModal(true);
    }
  };

  const handleMergeConfirm = (name) => {
    onMergeContacts([...selected], name);
    setSelected(new Set());
    setShowMergeModal(false);
  };

  const selectedNames = useMemo(() =>
    contacts.filter((c) => selected.has(c.id)).map((c) => c.name),
  [selected, contacts]);

  const sortedContacts = [...contacts].sort((a, b) => {
    const aHasPhone = !!a.phone?.trim();
    const bHasPhone = !!b.phone?.trim();
    if (aHasPhone !== bHasPhone) return aHasPhone ? 1 : -1;
    return a.name.localeCompare(b.name);
  });
  const filledCount = Object.values(phones).filter((p) => p.trim()).length;
  let firstEmptyFound = false;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="relative bg-white rounded-xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <div>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Phone className="w-5 h-5 text-indigo-600" />
                Edit Contacts
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {filledCount}/{sortedContacts.length} have phone numbers
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Merge toolbar */}
          {selected.size >= 2 && (
            <div className="flex items-center justify-between mx-4 mt-3 p-2.5 bg-indigo-50 rounded-lg border border-indigo-200">
              <p className="text-xs font-medium text-indigo-700">{selected.size} selected</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  data-testid="bulk-merge-selected-btn"
                  onClick={handleMergeClick}
                  className="gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7 px-2.5"
                >
                  <Merge className="w-3.5 h-3.5" />Merge
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelected(new Set())}
                  className="text-xs h-7 px-2"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Contact list */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {sortedContacts.map((contact) => {
              const isFirstEmpty = !firstEmptyFound && !phones[contact.id]?.trim();
              if (isFirstEmpty) firstEmptyFound = true;
              const isConfirming = confirmDeleteId === contact.id;
              const isEditing = editingNameId === contact.id;
              const isSelected = selected.has(contact.id);

              return (
                <div
                  key={contact.id}
                  className={`flex items-center gap-2 p-3 rounded-lg transition-colors ${
                    isConfirming ? "bg-red-50 border border-red-200" :
                    isSelected ? "bg-indigo-50 border border-indigo-200" :
                    "bg-slate-50 hover:bg-slate-100"
                  }`}
                >
                  {isConfirming ? (
                    <div className="flex-1 flex items-center justify-between">
                      <p className="text-sm text-red-700 font-medium">
                        Delete <span className="font-bold">{contact.name}</span>?
                      </p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" data-testid={`cancel-delete-contact-${contact.id}`} onClick={() => setConfirmDeleteId(null)} className="h-7 text-xs">Cancel</Button>
                        <Button variant="destructive" size="sm" data-testid={`confirm-delete-inline-${contact.id}`} onClick={() => handleConfirmDelete(contact.id)} className="h-7 text-xs bg-red-600 hover:bg-red-700">Delete</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Checkbox
                        checked={isSelected}
                        data-testid={`bulk-select-${contact.id}`}
                        onCheckedChange={() => toggleSelect(contact.id)}
                        className="h-4 w-4 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="flex items-center gap-1.5">
                            <Input
                              ref={nameInputRef}
                              data-testid={`rename-input-${contact.id}`}
                              value={editNameValue}
                              onChange={(e) => setEditNameValue(e.target.value)}
                              onKeyDown={(e) => handleRenameKeyDown(e, contact.id)}
                              onBlur={() => handleSaveRename(contact.id)}
                              className="text-sm h-7 font-medium"
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 group cursor-pointer" onClick={() => handleStartRename(contact)}>
                            <p className="font-medium text-slate-900 text-sm truncate">{contact.name}</p>
                            <Pencil className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                          </div>
                        )}
                        <p className="text-xs text-slate-400">
                          {contact.postcodes?.length ? contact.postcodes.join(', ') : contact.postcode}
                        </p>
                      </div>
                      <Input
                        ref={isFirstEmpty ? firstInputRef : undefined}
                        data-testid={`phone-input-${contact.id}`}
                        type="tel"
                        inputMode="tel"
                        placeholder="Phone"
                        value={phones[contact.id] || ""}
                        onChange={(e) => setPhones((prev) => ({ ...prev, [contact.id]: e.target.value }))}
                        className="w-28 sm:w-36 text-sm h-8"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`bulk-delete-contact-${contact.id}`}
                        onClick={() => setConfirmDeleteId(contact.id)}
                        className="text-slate-400 hover:text-red-500 hover:bg-red-50 h-7 w-7 shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-100 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button
              data-testid="save-phones-btn"
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleSave}
              disabled={saving || saved}
            >
              {saved ? (<><Check className="w-4 h-4 mr-1" /> Saved</>) : saving ? "Saving..." : (<><Save className="w-4 h-4 mr-1" /> Save All</>)}
            </Button>
          </div>

          {/* Merge Name Picker (overlays inside this modal) */}
          <AnimatePresence>
            {showMergeModal && (
              <MergeNameModal
                names={selectedNames}
                onConfirm={handleMergeConfirm}
                onClose={() => setShowMergeModal(false)}
              />
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
